import { Flex, Title, Paper, Stack, Text } from "@mantine/core";
import {
  createFileRoute,
  redirect,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";

import {
  useStartLogin,
  useGetLoginStatus,
  useStartSignIn,
  useStartSignUp,
} from "../hooks/api/";
import { useEffect, useState, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useUpdateCustomerData } from "../hooks/ui/";
import { BankIdQrCode } from "../components/bankID/qrCode";
import { LoginMessage } from "../components/login/loginMessage";
import { isMobileDevice } from "../utils/useDeviceDetection";
import { FooterButtons } from "../components/footerButtons";
import { Button } from "@mantine/core";
import { BankIdPendingMessage } from "../components/login/pendingMessage";
import { LoginActions } from "../components/login/loginActions";
import { LoaderComponent } from "../components/loaderComponent";
import {
  navigateByCustomerStatus,
  prefetchCustomerData,
} from "../utils/navigateByCustomerStatus";
import { v4 as uuidV4 } from "uuid";
import { SigningComplete } from "../components/signingComplete";

const initialHash = window.location.hash;

export const Route = createFileRoute("/login")({
  component: LoginPage,
  beforeLoad: ({ context }) => {
    if (context.credentials?.sessionToken) {
      redirect({
        to: "/home",
        search: true,
      });
    }
  },
});

function LoginPage() {
  const navigate = useNavigate();
  const router = useRouter();
  const signIn = useStartSignIn();
  const signUp = useStartSignUp();
  const [complete, setComplete] = useState(false);
  const [nonceError, setNonceError] = useState(false);
  const [completedInOtherTab, setCompletedInOtherTab] = useState(false);

  const [returnedFromBankId] = useState(() => {
    const hash = initialHash || window.location.hash;
    const hasNonce = /[#&]nonce=/.test(hash);
    if (!hasNonce) return false;
    // If sessionStorage has the marker, this is the same tab that started BankID — not a "new tab" redirect
    if (sessionStorage.getItem("bankid_login_tab")) return false;
    // This is a new tab or different browser opened by BankID.
    // The original tab is still polling and will complete the login.
    localStorage.removeItem("bankid_login_nonce");
    return true;
  });

  const [resumedTransactionId, setResumedTransactionId] = useState<
    string | null
  >(() => {
    if (returnedFromBankId) return null;
    const hash = initialHash || window.location.hash;
    const hasNonce = /[#&]nonce=/.test(hash);
    if (hasNonce) {
      const storedTransactionId = localStorage.getItem(
        "bankid_login_transaction_id",
      );
      if (storedTransactionId) return storedTransactionId;
    }
    return null;
  });
  const startLogin = useStartLogin();

  const resolvedTransactionId =
    resumedTransactionId ?? startLogin.data?.body?.id;
  const getLoginStatus = useGetLoginStatus({
    id: resolvedTransactionId,
    enabled:
      (startLogin.isSuccess || resumedTransactionId !== null) && !complete,
  });
  const {
    updatePersonalNumber,
    updateTransactionId,
    updateCredentials,
    updateName,
    updateIdentityVerificationId,
  } = useUpdateCustomerData();

  const queryClient = useQueryClient();
  const isMobile = isMobileDevice();
  const nonceRef = useRef<string | null>(null);

  const processStatusInfo =
    getLoginStatus.data?.body?.providerInfo?.seBankID?.processStatusInfo;

  // Store transactionId in localStorage so the new tab BankID opens can resume polling
  useEffect(() => {
    if (startLogin.isSuccess && startLogin.data?.body?.id) {
      localStorage.setItem(
        "bankid_login_transaction_id",
        startLogin.data.body.id,
      );
      sessionStorage.setItem("bankid_login_tab", "1");
    }
  }, [startLogin.isSuccess, startLogin.data]);

  // Clean up localStorage once sign-in resolves successfully.
  // Also send a signal so the original tab knows that login completed in this new tab opened by BankID.
  useEffect(() => {
    if (signIn.isSuccess) {
      localStorage.setItem("bankid_login_complete", Date.now().toString());
      localStorage.removeItem("bankid_login_nonce");
      localStorage.removeItem("bankid_login_transaction_id");
      localStorage.removeItem("bankid_login_complete");
      sessionStorage.removeItem("bankid_login_tab");
    }
  }, [signIn.isSuccess]);

  // Listen for the cross-tab signal: if the new tab completes login,
  // stop polling in the original tab and show a "logged in elsewhere" state.
  useEffect(() => {
    const onStorageChange = (e: StorageEvent) => {
      if (e.key === "bankid_login_complete" && e.newValue) {
        // Login finished in the other tab, stop everything here.
        setComplete(true);
        setCompletedInOtherTab(true);
      }
    };
    window.addEventListener("storage", onStorageChange);
    return () => window.removeEventListener("storage", onStorageChange);
  }, []);

  // Use refs so event handlers always access the latest state without
  // needing to re-register listeners (which causes missed events on iOS resume).
  const completeRef = useRef(complete);
  completeRef.current = complete;
  const getLoginStatusRef = useRef(getLoginStatus);
  getLoginStatusRef.current = getLoginStatus;
  const signInRef = useRef(signIn);
  signInRef.current = signIn;

  // On iOS, when the user leaves for BankID app, the tab is fully suspended.
  // When they return, we need to force a refetch. Using refs ensures the handler
  // is registered once and never misses the visibilitychange event.
  useEffect(() => {
    const handleResume = () => {
      if (document.visibilityState !== "visible") return;

      const loginStatus = getLoginStatusRef.current;
      const currentSignIn = signInRef.current;
      const isComplete = completeRef.current;

      // Clean nonce from hash if present
      const fragment = window.location.hash;
      if (/[#&]nonce=/.test(fragment)) {
        window.history.replaceState(
          null,
          "",
          window.location.pathname + window.location.search,
        );
      }

      // If polling completed but signIn failed previously, retry it
      if (
        isComplete &&
        loginStatus.data?.body?.status === "complete" &&
        !currentSignIn.isPending &&
        !currentSignIn.isSuccess
      ) {
        const completionData =
          loginStatus.data?.body?.providerInfo?.seBankID?.completionData?.user;
        const personalNumber = completionData?.personalNumber;
        const transactionId = loginStatus.data?.body?.id;
        if (personalNumber && transactionId) {
          currentSignIn.mutate({ personalNumber, transactionId });
        }
      } else if (!isComplete) {
        loginStatus.refetch();
      }
    };

    const onHashChange = () => {
      // When BankID returns to the same tab (hash change without reload)
      const fragment = window.location.hash;
      if (/[#&]nonce=/.test(fragment)) {
        window.history.replaceState(
          null,
          "",
          window.location.pathname + window.location.search,
        );
      }
      // Force refetch regardless, the hash change means BankID completed
      if (!completeRef.current) {
        getLoginStatusRef.current.refetch();
      }
    };

    document.addEventListener("visibilitychange", handleResume);
    window.addEventListener("hashchange", onHashChange);
    return () => {
      document.removeEventListener("visibilitychange", handleResume);
      window.removeEventListener("hashchange", onHashChange);
    };
  }, []);

  useEffect(() => {
    if (returnedFromBankId) return;

    // If BankID redirected back via returnUrl, the URL fragment will contain the nonce.
    // Validate it against what we stored in localStorage before starting the order.
    const fragment = window.location.hash;
    const match = fragment.match(/[#&]nonce=([^&]+)/);
    if (match) {
      const returnedNonce = match[1];
      const storedNonce = localStorage.getItem("bankid_login_nonce");
      const storedTransactionId = localStorage.getItem(
        "bankid_login_transaction_id",
      );

      if (storedNonce && returnedNonce !== storedNonce) {
        // Nonce exists in this browser but doesn't match, block the flow.
        setNonceError(true);
        setComplete(true);
      } else if (storedTransactionId) {
        // Nonce matches, resume polling with the stored transaction.
        setResumedTransactionId(storedTransactionId);
      }
      localStorage.removeItem("bankid_login_nonce");
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search,
      );
    } else {
      // Only clear the query cache when this is a fresh page load (no BankID redirect).
      queryClient.clear();
    }
  }, [queryClient, returnedFromBankId]);

  const mobileInstructionText = "Starta BankID-appen på din mobil";
  const desktopInstructionText =
    "Starta BankID-appen på mobila enhet och tryck på 'Skanna QR-kod'. Rikta kameran mot QR-koden och signera.";

  useEffect(() => {
    if (getLoginStatus.data?.body?.status === "failed") {
      setComplete(true);
    }
    if (getLoginStatus.isError) {
      setComplete(true);
    }
    if (getLoginStatus.data?.body?.status === "complete") {
      setComplete(true);

      // If login already completed in the new tab, don't sign in again here.
      if (completedInOtherTab) return;

      const completionData =
        getLoginStatus.data?.body?.providerInfo?.seBankID?.completionData?.user;
      const pn = completionData?.personalNumber;
      const tid = getLoginStatus.data?.body?.id;
      updatePersonalNumber(pn);
      updateTransactionId(tid);
      updateName(
        completionData?.givenName ?? "",
        completionData?.surname ?? "",
      );
      signIn.mutate({ personalNumber: pn, transactionId: tid });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getLoginStatus.data, getLoginStatus.isError]);

  // This handles the case where iOS kills the TCP connection during tab suspension,
  // causing the first signIn request to fail. Retry up to 3 times with backoff.
  const signInRetryCount = useRef(0);
  useEffect(() => {
    if (
      signIn.isError &&
      getLoginStatus.data?.body?.status === "complete" &&
      !completedInOtherTab &&
      signInRetryCount.current < 3
    ) {
      const completionData =
        getLoginStatus.data?.body?.providerInfo?.seBankID?.completionData?.user;
      const personalNumber = completionData?.personalNumber;
      const transactionId = getLoginStatus.data?.body?.id;
      if (personalNumber && transactionId) {
        const delay = Math.min(1000 * 2 ** signInRetryCount.current, 4000);
        const timer = setTimeout(() => {
          signInRetryCount.current += 1;
          signIn.mutate({ personalNumber, transactionId });
        }, delay);
        return () => clearTimeout(timer);
      }
    }
    if (signIn.isSuccess) {
      signInRetryCount.current = 0;
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signIn.isError, signIn.isSuccess]);

  const navigateToSignup = useCallback(() => {
    navigate({
      to: "/signup",
      search: {
        ...(window.location.search
          ? Object.fromEntries(new URLSearchParams(window.location.search))
          : {}),
        from: "login",
      },
    });
  }, [navigate]);

  useEffect(() => {
    if (signIn.data?.isCustomer === false) {
      if (signIn.data.credentials) {
        // Non-customer with credentials, store and navigate
        updateCredentials(signIn.data.credentials);
        // Capture the CAS verification id from auth so it isn't lost when this /login → /signup
        // path skips the IDENTIFY step; POST /user/user needs it to link KYC screening to CAS.
        updateIdentityVerificationId(
          signIn.data.identityVerificationId ?? null,
        );
        navigateToSignup();
      } else {
        // Non-customer without credentials, sign up first to obtain them
        const pn = useUpdateCustomerData.getState().customerData.personalNumber;
        const tid = useUpdateCustomerData.getState().customerData.transactionId;
        signUp.mutate({ personalNumber: pn, transactionId: tid });
      }
    } else if (signIn.data?.credentials) {
      updateCredentials(signIn.data.credentials);
      updateIdentityVerificationId(signIn.data.identityVerificationId ?? null);

      const credentials = signIn.data.credentials;

      prefetchCustomerData(credentials, queryClient);

      const searchParams = window.location.search
        ? Object.fromEntries(new URLSearchParams(window.location.search))
        : undefined;

      navigateByCustomerStatus({
        credentials,
        queryClient,
        navigate,
        invalidate: () => router.invalidate(),
        searchParams,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signIn.isSuccess, signIn.isError]);

  useEffect(() => {
    if (signUp.isSuccess && signUp.data?.credentials) {
      updateCredentials(signUp.data.credentials);
      updateIdentityVerificationId(signUp.data.identityVerificationId ?? null);
      navigateToSignup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signUp.isSuccess, signUp.data]);

  if (
    signIn.isPending ||
    signIn.isSuccess ||
    signUp.isPending ||
    signUp.isSuccess
  ) {
    return <LoaderComponent />;
  }

  if (
    complete &&
    getLoginStatus.data?.body?.status === "complete" &&
    !completedInOtherTab &&
    signInRetryCount.current < 3
  ) {
    return <LoaderComponent />;
  }

  if (returnedFromBankId) {
    return (
      <SigningComplete
        title="Inloggning klar!"
        description="Inloggningen har genomförts. Du kan stänga den här fliken och gå tillbaka till din ursprungliga flik."
        showCloseButton
      />
    );
  }

  // If login completed in the new tab that BankID opened (e.g. Chrome on iOS),
  // show a message in the original tab so the user knows where to go.
  if (completedInOtherTab) {
    return (
      <Flex justify={"center"} px={"lg"} pt={isMobile ? "2rem" : "7rem"}>
        <Stack maw={"770px"} w={"100%"} align="center" gap="md">
          <Title
            order={1}
            size="h2"
            ff={"var(--wallet-font-family-sambla-sans)"}
            ta="center"
          >
            Inloggningen lyckades!
          </Title>
          <Text ta="center">
            Din inloggning slutfördes i en annan flik. Du kan stänga den här
            fliken.
          </Text>
        </Stack>
      </Flex>
    );
  }

  if (
    !complete &&
    getLoginStatus.data?.body?.status === "started" &&
    (processStatusInfo === "userSign" || processStatusInfo === "started")
  ) {
    return (
      <Paper p={30} radius={"24px"} mx={"lg"} mt={isMobile ? "2rem" : "7rem"}>
        <BankIdPendingMessage
          onCancel={() => setComplete(true)}
          processStatusInfo={processStatusInfo}
        />
      </Paper>
    );
  }

  return (
    <Flex justify={"center"} px={"lg"} pt={isMobile ? "2rem" : "7rem"}>
      <Paper p={30} radius={"24px"}>
        {startLogin.isSuccess && !complete ? (
          <Stack>
            <Title
              order={1}
              size="h2"
              ff={"var(--wallet-font-family-sambla-sans)"}
              mb={"md"}
            >
              Var vänlig legitimera dig med BankID
            </Title>
            <BankIdQrCode
              isLoading={getLoginStatus.isLoading}
              qrData={getLoginStatus.data?.body?.providerInfo?.seBankID?.qrData}
              autoStartToken={
                getLoginStatus.data?.body?.providerInfo?.seBankID
                  ?.autoStartToken
              }
              instructionText={
                isMobile ? mobileInstructionText : desktopInstructionText
              }
              processStatusInfo={processStatusInfo}
              nonce={nonceRef.current}
            />

            <FooterButtons>
              <Button variant="default" onClick={() => setComplete(true)}>
                Avbryt
              </Button>
            </FooterButtons>
          </Stack>
        ) : resumedTransactionId !== null && !complete ? (
          <LoaderComponent />
        ) : (
          <Stack maw={"770px"} w={"100%"}>
            {nonceError ? (
              <Text c="red" ta="center">
                Säkerhetskontrollen misslyckades. Vänligen försök att logga in
                igen.
              </Text>
            ) : (
              <LoginMessage
                status={getLoginStatus.data?.body?.status}
                processStatusInfo={processStatusInfo}
              />
            )}
            <LoginActions
              onLogin={() => {
                setComplete(false);
                setNonceError(false);
                const nonce = uuidV4();
                nonceRef.current = nonce;
                localStorage.setItem("bankid_login_nonce", nonce);
                sessionStorage.setItem("bankid_login_tab", "1");
                startLogin.mutateAsync({ nonce });
              }}
              loginLoading={startLogin.isPending}
            />
          </Stack>
        )}
      </Paper>
    </Flex>
  );
}
